<template>
  <section v-if="current" class="activity-recording">
    <WorkoutSessionOverview v-if="!integrated" :duration-label="durationLabel" :energy="recording ? energy : null" :distance="recording && recording.distanceMeters" :recording="recording">
      <ImportedWorkoutPulse v-if="recording" slot="pulse-details" :recording="recording" :owner-id="ownerId" />
    </WorkoutSessionOverview>
    <p v-if="reading" class="recording-caption" role="status">Messwerte werden geladen …</p>
    <p v-else-if="error" class="recording-caption" role="status">{{ error }} <button type="button" @click="loadRecording">Erneut laden</button></p>
    <details class="recording-info">
      <summary>Aufzeichnung & Quelle<v-icon size="18">mdi-chevron-down</v-icon></summary>
      <p>{{ current.sourceNames.join(' + ') }}</p>
      <p v-if="current.resolution === 'confirmed'">Zusammengeführt auf diesem Gerät</p>
      <label v-if="references.length > 1" class="recording-picker">Aufzeichnung<select v-model="selectedReference"><option v-for="reference in references" :key="reference.workoutId" :value="reference.workoutId">{{ reference.label }}</option></select></label>
      <button v-if="current.resolution === 'confirmed'" type="button" @click="undo">Zuordnung lösen</button>
    </details>
  </section>
</template>
<script>
import service from "@/services/customerHubActivityService"
import preferences from "@/services/customerHubPreferences"
import healthAdapter from "@/services/customerHubHealthAdapter"
import healthActivity from "@/services/healthActivityService"
import { healthSourceLabel, healthWorkoutEnergy } from "@/services/healthPresentation"
import ImportedWorkoutPulse from "./ImportedWorkoutPulse.vue"
import WorkoutSessionOverview from "./WorkoutSessionOverview.vue"

export default {
  components: { ImportedWorkoutPulse, WorkoutSessionOverview },
  props: { activity: { type: Object, default: null }, integrated: { type: Boolean, default: false }, durationLabel: { type: String, default: "" } },
  data: () => ({ selectedReference: "", recording: null, reading: false, error: "", request: 0 }),
  computed: {
    ownerId() { return preferences.identity().userId },
    current() { return service.entries().find(item => item.id === this.activity?.id) || null },
    references() {
      if (!this.current || !healthAdapter.scope() || !healthActivity.scope()) return []
      const unique = new Map()
      for (const reference of this.current.healthLinks || []) {
        if (reference.provider !== "health_connect" || !/^hc:[A-Za-z0-9_-]{1,342}$/.test(reference.workoutId)) continue
        const source = healthAdapter.state.workouts.find(item => item.workoutId === reference.workoutId)
        unique.set(reference.workoutId, { ...reference, label: source ? healthSourceLabel(source) + " · " + this.$moment(source.startedAt).format("HH:mm") : "Health Connect" })
      }
      return [...unique.values()]
    },
    reference() { return this.references.find(item => item.workoutId === this.selectedReference) || this.references[0] || null },
    readKey() { return [preferences.identity().userId, preferences.identity().token, healthAdapter.scope(), healthActivity.scope(), this.current?.id, this.reference?.workoutId].join("|") },
    energy() { return healthWorkoutEnergy(null, this.recording) },
  },
  watch: { readKey: { immediate: true, handler() { this.loadRecording() } } },
  beforeDestroy() { this.request++; this.$emit("recording", null) },
  methods: {
    number(value) { return Math.round(value).toLocaleString("de-CH") },
    hasNumber(value) { return typeof value === "number" && Number.isFinite(value) && value >= 0 },
    undo() { service.undoMerge(this.current); this.$emit("separated") },
    async loadRecording() {
      const request = ++this.request, key = this.readKey, reference = this.reference
      this.recording = null; this.error = ""; this.reading = false; this.$emit("recording", null)
      if (!reference) return
      this.reading = true
      try {
        const recording = await healthActivity.read(reference.workoutId)
        if (request !== this.request || key !== this.readKey) return
        if (recording?.provider === reference.provider && recording.workoutId === reference.workoutId) { this.recording = recording; this.$emit("recording", recording) }
        else this.error = "Für diese Aufzeichnung sind gerade keine Messwerte verfügbar."
      } catch (_) {
        if (request === this.request && key === this.readKey) this.error = "Messwerte sind gerade nicht verfügbar."
      } finally { if (request === this.request) this.reading = false }
    },
  },
}
</script>
<style scoped>
.activity-recording { color: #f5f5f5; }
.recording-info { margin-top: 24px; border-top: 1px solid #ffffff12; }
.recording-info summary { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 48px; color: #bbb; font-size: 12px; cursor: pointer; list-style: none; }
.recording-info summary::-webkit-details-marker { display: none; }.recording-info summary .v-icon { color: #999; }.recording-info[open] > summary .v-icon { transform: rotate(180deg); }
.recording-info p, .recording-caption { color: #aaa; font-size: 12px; line-height: 1.65; margin: 6px 0; }
.activity-recording button { min-height: 44px; padding: 8px 0; color: #ccc; font-size: 12px; }
.recording-picker { display: grid; gap: 6px; font-size: 11px; color: #aaa; margin-top: 12px; }.recording-picker select { padding: 12px; background: #333; border-radius: 10px; color: white; width: 100%; color-scheme: dark; }
button:focus-visible, summary:focus-visible, select:focus-visible { outline: 2px solid var(--v-primary-base, #00963f); outline-offset: 3px; }
</style>
